import React from 'react';

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';

import { loadFont } from 'graphics';
import { selectStats } from 'logic/stats';
import { Color } from 'common';
import Tooltip from 'ui/tooltip';
import { useCtrl, useShift } from 'ui/modifiers';
import TooltipBuilder, { colored } from 'tooltips/builder';
import { profileSelectSkills } from 'logic/stateReducers';

import './SkillTree.scss';
import { EmbedProfileLink, useContentCreator, useProfileSetId } from 'components/cclink';
import GameContext from 'gamectx';
import SkillsBackground from './SkillsBackground';
import { memoizedCompute } from 'utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import AdUnit from 'AdUnit';
import { checkPrereq, gridPos, questPoints, SkillButton, SkillSlider, skillTreeNames } from './utils';

const _L = Locale("ui-character");

const loadSkillIcons = memoizedCompute((charClass, gfx) => {
  const dc6 = gfx.itemdc6(Data.classSkillIcons[charClass]);
  const colors = [Color.GRAY, Color.WHITE, Color.BLUE, Color.RED, Color.GREEN];
  if (gfx.hd) colors.push(Color.BLACK);
  return Promise.all(colors.map(clr => dc6.then(ic => gfx.makeBitmap(ic, 9 * 21 + clr)))).then(results => {
    if (!gfx.hd) results.push(results[2]);
    return results;
  });
});

const SkillTreeInfoTip = () => new TooltipBuilder()
  .line(_L`Hold Shift to automatically raise character level`)
  .line(_L`Hold Ctrl to show base skill levels`)
  .line(_L`Click on skill levels to input number directly`)
  .build();

export default function SkillTree() {
  const charClass = Store.useProfile(profile => profile.class);
  const charLevel = Store.useProfile(profile => profile.level);
  const quests = Store.useProfile(profile => profile.quests);
  const stats = Store.useSelector(store => selectStats(store));
  const canvasRef = React.useRef();
  const ctrlKey = useCtrl();
  const shiftKey = useShift();
  const gameCtx = React.useContext(GameContext);
  const gfx = gameCtx.gfx;

  const isCC = useContentCreator();
  const profileId = useProfileSetId();

  const [recording, setRecording] = React.useState(false);
  const bypassLevel = shiftKey || recording;

  const classRef = React.useRef();
  const [fonts, setFonts] = React.useState();
  const [icons, setIcons] = React.useState();
  const [hover, setHover] = React.useState();
  const [pressed, setPressed] = React.useState();
  const [highlight, setHighlight] = React.useState();

  React.useEffect(() => {
    Promise.all([loadFont('font16'), loadFont('fontformal10')]).then(fonts => {
      if (canvasRef.current) {
        setFonts(fonts);
      }
    });
  }, []);
  React.useEffect(() => {
    classRef.current = charClass;
    loadSkillIcons(charClass, gfx).then(icons => {
      if (classRef.current === charClass) {
        setIcons(icons);
      }
    });
    return () => classRef.current = null;
  }, [charClass, gfx]);

  const skills = stats.player.skills;
  const base_skills = stats.player.base_skills;
  const spent_points = Object.values(base_skills).reduce((sum, x) => sum + x, 0);
  const quest_points = questPoints(quests);
  const max_points0 = charLevel - 1 + quest_points;
  const max_points = (bypassLevel ? 99 : charLevel) - 1 + quest_points;
  const canLevel = spent_points < max_points;
  const reqLevel = Object.entries(base_skills).reduce((sum, [id, level]) => {
    const skill = Data.skills[id];
    if (level && skill) return Math.max(sum, skill.reqlevel + (level - 1));
    return sum;
  }, Math.max(1, 1 + spent_points - quest_points));

  React.useEffect(() => {
    if (!icons || !fonts) return;

    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    for (const [id, skill] of Object.entries(Data.skills)) {
      if (skill.charclass !== charClass) continue;
      const desc = Data.skillDesc[skill.skilldesc];
      if (!desc) continue;
      const page = desc.skillpage, row = desc.skillrow, col = desc.skillcolumn;
      if (!page || !row || !col) continue;

      let level = skills[id] || 0;
      const base_level = base_skills[id] || 0;
      if (ctrlKey) level = base_level;

      let color = level ? (base_level ? 1 : 2) : 0;
      if (hover === id && canLevel && base_level < 20) {
        if (!checkPrereq(id, base_skills) || (!bypassLevel && skill.reqlevel > charLevel)) {
          color = 3;
        } else if (bypassLevel || skill.reqlevel + base_level <= charLevel) {
          color = 5;
        }
      }

      if (highlight?.id === id) {
        if (highlight.delta > 0) {
          color = 4;
        } else {
          color = 3;
        }
      }

      const source = icons[color];
      if (!source) continue;
      const { bitmap, frames } = source;

      const frm = frames[desc.iconcel + (pressed === id ? 1 : 0)];
      if (!frm) continue;

      const { left: x, top: y } = gridPos(page, row, col);

      ctx.drawImage(bitmap, frm.x, frm.y, frm.width, frm.height, x, y, frm.width, frm.height);
      if (level && !gfx.hd) {
        const text = level.toString();
        const color = (base_level === level ? Color.WHITE : Color.BLUE);
        if (text.length === 1) {
          const width = fonts[0].measure(text);
          fonts[0].render(ctx, x + 52 - (width >> 1), y + 54, text, color);
        } else {
          const width = fonts[1].measure(text);
          fonts[1].render(ctx, x + 51 - (width >> 1), y + 60, text, color);
        }
      }
    }
  }, [fonts, icons, skills, base_skills, charClass, hover, pressed, ctrlKey, bypassLevel, canLevel, charLevel, highlight, gfx]);

  const tabPoints = [];
  for (const id in base_skills) {
    const skill = Data.skills[id];
    const desc = Data.skillDesc[skill?.skilldesc];
    if (!desc || !desc.skillpage) continue;
    tabPoints[desc.skillpage - 1] = (tabPoints[desc.skillpage - 1] || 0) + base_skills[id];
  }

  const dispatch = Store.useDispatch();
  const onChange = React.useCallback((id, value) => dispatch.setProfile(profile => {
    const quest_points = questPoints(profile.quests);
    const skills = {...profileSelectSkills(profile)};
    const spentNow = Object.values(skills).reduce((sum, x) => sum + x, 0);
    const spentNext = spentNow - (skills[id] || 0) + value;
    const increase = value > (skills[id] || 0);
    if (spentNext > 98 + quest_points && increase) {
      value -= (spentNext - 98 - quest_points);
      if (value < (skills[id] || 0)) return profile;
    }
    if (!value && Object.entries(skills).some(([sid, level]) => {
      if (!level) return false;
      const skill = Data.skills[sid];
      return (skill.reqskill1 === id || skill.reqskill2 === id || skill.reqskill3 === id);
    })) {
      return profile;
    }
    if (recording) {
      if (increase) {
        profile = {...profile};
        profile.skillProgression = profile.skillProgression?.slice() || [];
        profile.skillProgression.push(id);
        profile.skillPos = profile.skillProgression.length;
      }
    } else {
      if (value) {
        skills[id] = value;
      } else {
        delete skills[id];
      }
      profile = {...profile, skills};
      delete profile.skillProgression;
    }
    if (increase) {
      profile.level = Math.min(99, Math.max(profile.level, spentNext - quest_points + 1, (Data.skills[id]?.reqlevel || 1) + value - 1));
    }
    return profile;
  }), [dispatch, recording]);
  const onReset = React.useCallback(() => dispatch.setProfile(profile => ({...profile, skills: {}, skillProgression: null, skillPos: 0})), [dispatch]);

  return <>
    <div className={`d2p-SkillTree d2p-game-${gameCtx.name}`}>
      <div className="d2p-skill-tabs">
        {[2, 1, 0].map(tab => (
          <div className="d2p-skill-tab" key={tab}>
            <div>{skillTreeNames[charClass][tab].map(str => Locale.strings[str]).join(' ')}</div>
            <div>{_L`${tabPoints[tab] || 0} Points Spent`}</div>
          </div>
        ))}
      </div>
      <div className="d2p-skills-container">
        {gfx.hd ? <SkillsBackground charClass={charClass}/> : <div className={`d2p-skills-background d2p-class-${charClass}`}/>}
        <canvas width={696} height={432} ref={canvasRef} style={{position: 'absolute'}}/>
        {Object.keys(Data.skills).filter(id => Data.skills[id].charclass === charClass).map(id => (
          <SkillButton key={id} id={id} stats={stats.player} onHover={setHover} onPressed={setPressed}
            canLevel={canLevel} bypassLevel={bypassLevel} onChange={onChange} recording={recording} hd={gfx.hd}/>
        ))}
      </div>
      <SkillSlider recording={recording} setRecording={setRecording} setHighlight={setHighlight}/>
    </div>
    <div className="d2p-SkillInfo">
      <div>
        <Tooltip tooltip={<SkillTreeInfoTip/>}>
          <FontAwesomeIcon icon={faQuestionCircle} className="d2p-info-tip"/>
        </Tooltip>
        {_L`Remaining points: ${colored(Math.max(0, max_points0 - spent_points), max_points0 > spent_points ? Color.WHITE : Color.GRAY)}`}
      </div>
      {reqLevel > 1 && <div>{_L`Required level: ${colored(reqLevel, reqLevel > charLevel ? Color.RED : Color.WHITE)}`}</div>}
      {spent_points > 0 && !recording && <div><span className="d2p-reset" onClick={onReset}>{_L`Reset all skills`}</span></div>}
      {!!(isCC && profileId) && <EmbedProfileLink {...profileId} type='skills'>Embed</EmbedProfileLink>}
      <AdUnit id="d2planner-tab-character" className="d2p-TabFooter d2planner-tab-ad"/>
    </div>
  </>;
}
